'use client';
'use no memo';

import { flexRender, type Table } from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';
import clsx from 'clsx';
import { useEffect, useRef } from 'react';

import { DataTestIds, type OrderDirection } from '@repo/frontend-shared';
import { SDSInternalSpinner } from '@repo/sds/components/_internal/SDSInternalSpinner';
import { SDSFlexbox } from '@repo/sds/components/SDSFlexbox';
import { SDSTableCell } from '@repo/sds/components/SDSTable/SDSTableCell';
import { SDSTableHeader } from '@repo/sds/components/SDSTable/SDSTableHeader';
import { SDSTableRow } from '@repo/sds/components/SDSTable/SDSTableRow';
import type { SDSTableFeatures, TableData } from '@repo/sds/components/SDSTable/types/table.types';
import {
  getCellDataTestId,
  getColumnAlign,
  getColumnWidth,
  getHeaderDataTestId,
} from '@repo/sds/components/SDSTable/utils/header-utils';
import { tanStackSortToOrderDirection } from '@repo/sds/components/SDSTable/utils/sort-utils';

interface SDSTableVirtualizedProps<T extends TableData> {
  table: Table<SDSTableFeatures, T>;
  data: T[];
  rowHeight: number;
  onRowClick?: (row: T) => void;
  onLoadMore?: () => void;
  paginationKey: string;
  hasMore?: boolean;
  isLoading?: boolean;
  filterable?: boolean;
  globalFilter?: string;
  setGlobalFilter?: (value: string) => void;
  className?: string;
  dataTestId?: string;
  sortField?: string;
  sortDirection?: OrderDirection;
  onSort?: (field: string) => void;
}

export function SDSTableVirtualized<T extends TableData>({
  table,
  data,
  rowHeight,
  onRowClick,
  onLoadMore,
  paginationKey,
  hasMore = false,
  isLoading = false,
  filterable = false,
  globalFilter,
  setGlobalFilter,
  className,
  dataTestId,
  sortField,
  sortDirection,
  onSort,
}: SDSTableVirtualizedProps<T>) {
  const isExternalSorting = sortField !== undefined && onSort !== undefined;
  const containerRef = useRef<HTMLDivElement>(null);
  const loadMoreLockRef = useRef<{ paginationKey: string; rowCount: number } | null>(null);
  const observedLoadingRef = useRef(false);

  useEffect(() => {
    const loadMoreLock = loadMoreLockRef.current;
    if (!loadMoreLock) return;
    if (loadMoreLock.paginationKey !== paginationKey || data.length !== loadMoreLock.rowCount) {
      loadMoreLockRef.current = null;
      observedLoadingRef.current = false;
      return;
    }

    if (isLoading) {
      observedLoadingRef.current = true;
      return;
    }

    if (observedLoadingRef.current) {
      loadMoreLockRef.current = null;
      observedLoadingRef.current = false;
    }
  }, [data.length, isLoading, paginationKey]);

  const virtualizer = useVirtualizer({
    count: data.length,
    getScrollElement: () => containerRef.current,
    estimateSize: () => rowHeight,
    overscan: 5,
    onChange: instance => {
      if (!hasMore) {
        loadMoreLockRef.current = null;
        observedLoadingRef.current = false;
        return;
      }
      const lastItem = instance.getVirtualItems().at(-1);
      const shouldLoad = lastItem && lastItem.index >= data.length - 5 && !isLoading && onLoadMore;
      const loadMoreLock = loadMoreLockRef.current;
      const isCurrentDataLocked =
        loadMoreLock?.paginationKey === paginationKey && loadMoreLock.rowCount === data.length;
      if (!shouldLoad || isCurrentDataLocked) return;

      loadMoreLockRef.current = { paginationKey, rowCount: data.length };
      observedLoadingRef.current = false;
      onLoadMore();
    },
  });

  const virtualItems = virtualizer.getVirtualItems();

  const rows = table.getRowModel().rows;
  // Header and body are separate tables here, so a declared width only holds
  // if both size their columns the same way. Automatic layout lets a long
  // value widen its column, which would drift each row against the header and
  // against every other row. Applied only when a width was declared: a table
  // that declares none keeps the automatic sizing it has today.
  const hasDeclaredWidths = table.getAllColumns().some(column => getColumnWidth(column.columnDef.meta) !== undefined);
  const tableClassName = clsx('SDSTable', hasDeclaredWidths && 'SDSTable--fixed-layout');

  return (
    <div
      className={clsx('SDSTable__container', 'SDSTable__container--virtualized', className)}
      data-testid={dataTestId}
    >
      {filterable && setGlobalFilter && (
        <div className="SDSTable__filter">
          <input
            aria-label="Filter table rows"
            value={globalFilter ?? ''}
            onChange={e => setGlobalFilter(e.target.value)}
            placeholder="Filter..."
            className="SDSTable__filter-input"
          />
        </div>
      )}

      <div className="SDSTable__virtualized-wrapper">
        {/* Fixed header */}
        <table className={clsx(tableClassName, 'SDSTable--header-fixed')} data-testid={DataTestIds.SDS.SDS_TABLE}>
          <thead>
            {table.getHeaderGroups().map(headerGroup => (
              <SDSTableRow key={headerGroup.id} isHeader>
                {headerGroup.headers.map(header => {
                  const isExternalSort = isExternalSorting && header.column.id === sortField;
                  const sortDir =
                    isExternalSort && sortDirection
                      ? sortDirection
                      : tanStackSortToOrderDirection(header.column.getIsSorted());
                  return (
                    <SDSTableHeader
                      key={header.id}
                      sortable={header.column.getCanSort()}
                      sortDirection={sortDir}
                      align={getColumnAlign(header.column.columnDef.meta)}
                      style={{ width: getColumnWidth(header.column.columnDef.meta) }}
                      data-testid={getHeaderDataTestId(header.column.id, header.column.columnDef.meta)}
                      onClick={() => {
                        if (isExternalSorting && onSort && header.column.id !== '_selection') {
                          onSort(header.column.id);
                        } else {
                          header.column.toggleSorting();
                        }
                      }}
                    >
                      {flexRender(header.column.columnDef.header, header.getContext())}
                    </SDSTableHeader>
                  );
                })}
              </SDSTableRow>
            ))}
          </thead>
        </table>

        {/* Virtualized body */}
        <div ref={containerRef} className="SDSTable__virtual-container" style={{ height: '400px', overflow: 'auto' }}>
          <div
            style={{
              height: `${virtualizer.getTotalSize()}px`,
              width: '100%',
              position: 'relative',
            }}
          >
            {virtualItems.map(virtualItem => {
              const row = rows[virtualItem.index];

              return (
                <div
                  key={virtualItem.key}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: `${virtualItem.size}px`,
                    transform: `translateY(${virtualItem.start}px)`,
                  }}
                >
                  <table className={tableClassName}>
                    <tbody>
                      <SDSTableRow onClick={() => onRowClick?.(row.original)} clickable={Boolean(onRowClick)}>
                        {row.getVisibleCells().map(cell => (
                          <SDSTableCell
                            key={cell.id}
                            align={getColumnAlign(cell.column.columnDef.meta)}
                            style={{ width: getColumnWidth(cell.column.columnDef.meta) }}
                            data-testid={getCellDataTestId(cell.column.id, cell.column.columnDef.meta)}
                          >
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </SDSTableCell>
                        ))}
                      </SDSTableRow>
                    </tbody>
                  </table>
                </div>
              );
            })}
          </div>

          {/* Loading indicator */}
          {isLoading && (
            <SDSFlexbox className="SDSTable__loading" justifyContent="center" alignItems="center">
              <SDSInternalSpinner />
            </SDSFlexbox>
          )}
        </div>
      </div>
    </div>
  );
}
